import {ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, from, split} from '@apollo/client'
import {setContext} from '@apollo/client/link/context'
import {useMsal, useIsAuthenticated} from '@azure/msal-react'
import DebounceLink from 'apollo-link-debounce'
import {RetryLink} from '@apollo/client/link/retry'
import {onError} from '@apollo/client/link/error'
import {loginRequest} from '../Auth/auth-config'

const AsyncTokenLookup = async (instance, inProgress) => {
  const accounts = instance.getAllAccounts()
  if (accounts?.[0] && inProgress === 'none') {
    let result
    try {
      result = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [
          process.env.NODE_ENV === 'production' ? globalThis.__env.REACT_APP_SCOPES : process.env.REACT_APP_SCOPES
        ]
      })
    } catch (err) {
      console.log('Error while getting token using acquireTokenSilent ', err)
      instance.acquireTokenRedirect(loginRequest)
    }
    return result.accessToken
  } else if (!accounts?.[0] && inProgress === 'none') {
    instance.acquireTokenRedirect(loginRequest)
  }
  return null
}

const Apollo = (App) =>
  function Apolloed() {
    const {instance, inProgress} = useMsal()

    const isAuthenticated = useIsAuthenticated()

    const abortControllers = {}
    const withToken = setContext(async ({operationName}, {headers}) => {
      const msalToken = await AsyncTokenLookup(instance, inProgress)
      if (abortControllers[operationName]) {
        abortControllers[operationName].abort()
      }
      abortControllers[operationName] = new globalThis.AbortController()

      if (isAuthenticated) {
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${msalToken}`
          },
          fetchOptions: {signal: abortControllers[operationName].signal}
        }
      } else {
        instance.loginRedirect(loginRequest)
      }
    })

    const appApiUrl = createHttpLink({
      uri: `${
        process.env.NODE_ENV === 'production' ? globalThis.__env.REACT_APP_API_URL : process.env.REACT_APP_API_URL
      }/graphql`
    })
    const adminPanelApiUrl = createHttpLink({
      uri: `${
        process.env.NODE_ENV === 'production'
          ? globalThis.__env.REACT_APP_ADMIN_PANEL_URL
          : process.env.REACT_APP_ADMIN_PANEL_URL
      }/graphql/` // add this env variable in the .env file
    })
    // longitudinal link added below for longitudinal api and added REACT_APP_LONGITUDINAL_API_URL in env.
    const longitudinalApiUrl = createHttpLink({
      uri: `${
        process.env.NODE_ENV === 'production'
          ? globalThis.__env.REACT_APP_LONGITUDINAL_API_URL
          : process.env.REACT_APP_LONGITUDINAL_API_URL
      }/graphql/`
    })

    const client = new ApolloClient({
      link: from([
        new DebounceLink(200),
        new RetryLink({
          delay: {
            initial: 300,
            max: Infinity,
            jitter: true
          },
          attempts: {
            max: 5,
            retryIf: (error) => !!error
          }
        }),
        onError(({graphQLErrors, operation, forward}) => {
          if (graphQLErrors) {
            return forward(operation)
          }
        })
        // ****longitudinal API related changes. divided the split into 2 parts. The second split function checks if the clientName is longitudinal. If it is, the operation is sent to the longitudinalApiUrl. If it's not, the operation is sent to the appApiUrl.****
      ]).split(
        (operation) => {
          const clientName = operation.getContext().clientName
          return clientName === 'adminPanel'
        },
        withToken.concat(adminPanelApiUrl),
        split(
          (operation) => operation.getContext().clientName === 'longitudinal',
          withToken.concat(longitudinalApiUrl),
          withToken.concat(appApiUrl)
        )
      ),
      cache: new InMemoryCache({addTypename: false}),
      queryDeduplication: false,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore'
        },
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        }
      }
    })

    return (
      <ApolloProvider client={client}>
        <App />
      </ApolloProvider>
    )
  }

export default Apollo
